import { Divider, Grid, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Dropdown from '../../components/Dropdown/Dropdown';
import { FilterTagsList } from '../../components/FilterTag/FilterTagStyle';
import FilterIcon from '../../components/Icons/FilterIcon';
import ResetIcon from '../../components/Icons/ResetIcon';
import ClickableLinkWithIcon from '../../components/Text/ClickableLinkWithIcon';
import DevicesReportFiltersDrawer from './components/DevicesReportFiltersDrawer';
import DownloadMenu from '../../components/DownloadMenu/DownloadMenu';

import { SimStatus } from '../../models/UserDevicesModels';
import { FileFormat } from '../../models/ImportModels';

import { resetFilter, setFilter } from '../../stores/DevicesReportStore';
import { RootState } from '../../stores/Store';

import useDevicesReportFilters from './useDevicesReportFilters';
import usePortalConfig from '../../hooks/usePortalConfig/usePortalConfig';

interface DevicesReportFilterProps {
  isLoading: boolean;
  exportData: (format: FileFormat) => void;
  totalItems: number;
}
function DevicesReport({ isLoading, exportData, totalItems }: DevicesReportFilterProps) {
  const { configs } = usePortalConfig();
  const [t] = useTranslation();
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const filter = useSelector((state: RootState) => state.devicesReportReducer.filter);
  const { devicesStatusOptions, renderFilterTags } = useDevicesReportFilters();

  /**
   * Resets the filters
   */
  const resetFilters = () => {
    dispatch(resetFilter());
  };

  return (
    <>
      <Grid container rowGap="1.5rem" sx={{ paddingTop: '1.5rem' }}>
        <Grid item xl={5} md={6} xs={12}>
          <Dropdown
            id="type"
            name="type"
            label={t('pages.devicesReport.status')}
            value={filter.status}
            onChange={(e) => {
              dispatch(setFilter({ ...filter, status: e.target.value as SimStatus | '' }));
            }}
            items={devicesStatusOptions}
            placeholder={t('common.placeholders.dropdownPlaceholder') + t('pages.devicesReport.status').toLowerCase()}
          />
        </Grid>
      </Grid>
      <Divider
        sx={{
          borderColor: theme.palette.monochrome.monoChrome3,
          marginTop: '2.5rem',
          marginBottom: '2.5rem',
        }}
      />
      {/* Filters controls */}
      <Grid container rowGap="1.5rem" sx={{ paddingBottom: '1.5rem' }}>
        <Grid item sm={12} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
          <ClickableLinkWithIcon onClick={resetFilters} leftIcon={<ResetIcon stroke={theme.palette.state.linkHover} />}>
            {t('common.reset')}
          </ClickableLinkWithIcon>
          <ClickableLinkWithIcon
            wrapperSx={{ paddingLeft: '1rem', paddingRight: '1rem' }}
            sx={{ color: theme.palette.text.primary }}
            onClick={() => setShowFiltersDrawer(true)}
            leftIcon={<FilterIcon />}
          >
            {t('common.filter')}
          </ClickableLinkWithIcon>
          <DownloadMenu
            exportMaxNumberOfRows={configs.devicesReport.exportMaxNumberOfRows}
            disabled={isLoading || totalItems === 0}
            downloadCSV={() => exportData('csv')}
            downloadExcel={() => exportData('xlsx')}
            itemLength={totalItems}
          />
          {showFiltersDrawer ? (
            <DevicesReportFiltersDrawer open={showFiltersDrawer} onClose={() => setShowFiltersDrawer(false)} />
          ) : null}
        </Grid>
      </Grid>
      {/* Filter tags */}
      <FilterTagsList sx={{ paddingBottom: '1rem' }}>{renderFilterTags().map((n) => n)}</FilterTagsList>
    </>
  );
}

export default DevicesReport;
